import { useState } from "react";
import { Cloud, Download, Edit3, RefreshCw } from "lucide-react";
import { toast } from "@/lib/toast";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  useDisconnectSource,
  useListCollections,
  useMyCollections,
  usePollNow,
  usePollStatus,
  useSelectLibrary,
  useSetUnmatchedPolicy,
  useUpdateWatchedCollections,
  useValidateApiKey,
  type ZoteroCollectionItem,
} from "@/hooks/useZoteroSource";
import { useZoteroBulkImportModalStore } from "@/stores/zotero-bulk-import-modal-store";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { UpgradeCard } from "@/components/UpgradeCard";
import type {
  UnmatchedPolicy,
  ZoteroLibraryItem,
  ZoteroSourceResponse,
} from "@/types/zoteroSync";

interface Props {
  source: ZoteroSourceResponse | null | undefined;
}

export function ZoteroWebApiCard({ source }: Props) {
  const [apiKey, setApiKey] = useState("");
  const [libraries, setLibraries] = useState<ZoteroLibraryItem[]>([]);
  const [pickedKey, setPickedKey] = useState<string>("");
  const [collections, setCollections] = useState<ZoteroCollectionItem[]>([]);
  const [pickedCollections, setPickedCollections] = useState<Set<string>>(new Set());
  const [scopeMode, setScopeMode] = useState<"all" | "subset">("all");
  const validate = useValidateApiKey();
  const select = useSelectLibrary();
  const listCollections = useListCollections();
  const disconnect = useDisconnectSource();
  const { data: currentUser } = useCurrentUser();
  const paid = isPaidPlan(currentUser?.plan);

  const onValidate = async () => {
    try {
      const res = await validate.mutateAsync(apiKey);
      setLibraries(res.libraries);
      setCollections([]);
      setPickedCollections(new Set());
      setScopeMode("all");
      if (res.libraries.length === 1) {
        const l = res.libraries[0];
        setPickedKey(`${l.library_type}:${l.library_id}`);
      }
    } catch (err) {
      let detail: string | null = null;
      try {
        const httpErr = err as { response?: { json?: () => Promise<{ detail?: string }> } };
        if (httpErr?.response?.json) {
          const body = await httpErr.response.json();
          detail = body?.detail ?? null;
        }
      } catch {
        /* fall through to generic message */
      }
      toast.error("Zotero rejected that key", {
        description: detail ?? undefined,
      });
    }
  };

  const onLoadCollections = async () => {
    const lib = libraries.find(
      (l) => `${l.library_type}:${l.library_id}` === pickedKey,
    );
    if (!lib) return;
    try {
      const res = await listCollections.mutateAsync({
        api_key: apiKey,
        library_type: lib.library_type,
        library_id: lib.library_id,
      });
      setCollections(res.collections);
      setScopeMode("subset");
    } catch {
      toast.error("Couldn't load collections from Zotero");
    }
  };

  const onConnect = async () => {
    const lib = libraries.find(
      (l) => `${l.library_type}:${l.library_id}` === pickedKey,
    );
    if (!lib) return;
    const watched = scopeMode === "subset" ? Array.from(pickedCollections) : [];
    await select.mutateAsync({
      api_key: apiKey,
      library_type: lib.library_type,
      library_id: lib.library_id,
      watched_collection_ids: watched,
    });
    toast.success(
      watched.length > 0
        ? `Connected to ${lib.name} — watching ${watched.length} collection${watched.length === 1 ? "" : "s"}`
        : `Connected to ${lib.name} — watching whole library`,
    );
    setApiKey("");
    setLibraries([]);
    setPickedKey("");
    setCollections([]);
    setPickedCollections(new Set());
    setScopeMode("all");
  };

  const toggleCollection = (key: string) => {
    setPickedCollections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (source?.source_type === "web_api") {
    return <ConnectedWebApiCard source={source} onDisconnect={() => disconnect.mutate()} />;
  }

  if (currentUser && !paid) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Cloud className="size-4" /> Cloud (Web API)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <UpgradeCard
            feature="Zotero Cloud Sync"
            description="Connect your Zotero library and keep new PDFs flowing into PaperChase automatically."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Cloud className="size-4" /> Cloud (Web API)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Get a key at{" "}
          <a
            className="underline"
            href="https://zotero.org/settings/keys"
            target="_blank"
            rel="noopener noreferrer"
          >
            zotero.org/settings/keys
          </a>
          . Recommended for Safari users + grad students with shared supervisor
          libraries.
        </p>
        <Input
          placeholder="Zotero API key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
        />
        <Button
          size="sm"
          disabled={!apiKey || validate.isPending}
          onClick={onValidate}
        >
          {validate.isPending ? "Validating…" : "Validate"}
        </Button>

        {libraries.length > 0 && (
          <>
            <select
              value={pickedKey}
              onChange={(e) => {
                setPickedKey(e.target.value);
                setCollections([]);
                setPickedCollections(new Set());
                setScopeMode("all");
              }}
              className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
            >
              <option value="">Pick a library…</option>
              {libraries.map((l) => (
                <option
                  key={`${l.library_type}:${l.library_id}`}
                  value={`${l.library_type}:${l.library_id}`}
                >
                  {l.name} ({l.library_type})
                </option>
              ))}
            </select>

            {pickedKey && (
              <div className="space-y-2 rounded border border-border p-3">
                <div className="text-xs font-medium">What should we watch?</div>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    checked={scopeMode === "all"}
                    onChange={() => setScopeMode("all")}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">The whole library</span>
                    <span className="block text-xs text-muted-foreground">
                      Every PDF saved anywhere will sync. Best for personal libraries.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    checked={scopeMode === "subset"}
                    onChange={() => {
                      setScopeMode("subset");
                      if (collections.length === 0) onLoadCollections();
                    }}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">Specific collections only</span>
                    <span className="block text-xs text-muted-foreground">
                      Recommended for shared / supervisor libraries — pick which folders to watch.
                    </span>
                  </span>
                </label>

                {scopeMode === "subset" && (
                  <div className="ml-6 space-y-1">
                    {listCollections.isPending && (
                      <div className="text-xs text-muted-foreground">Loading collections…</div>
                    )}
                    {!listCollections.isPending && collections.length === 0 && (
                      <div className="text-xs text-muted-foreground">
                        No collections found in this library.
                      </div>
                    )}
                    {collections.length > 0 && (
                      <CollectionTree
                        collections={collections}
                        picked={pickedCollections}
                        onToggle={toggleCollection}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            <Button
              size="sm"
              disabled={
                !pickedKey ||
                select.isPending ||
                (scopeMode === "subset" && pickedCollections.size === 0)
              }
              onClick={onConnect}
            >
              {select.isPending ? "Connecting…" : "Connect"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface CollectionTreeProps {
  collections: ZoteroCollectionItem[];
  picked: Set<string>;
  onToggle: (key: string) => void;
}

function CollectionTree({ collections, picked, onToggle }: CollectionTreeProps) {
  // Render a flat list ordered so children follow their parents (depth-indented).
  // For now: alphabetize at each level via a simple depth-based render.
  const byParent = new Map<string | null, ZoteroCollectionItem[]>();
  for (const c of collections) {
    const arr = byParent.get(c.parent_key) ?? [];
    arr.push(c);
    byParent.set(c.parent_key, arr);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => a.name.localeCompare(b.name));
  }
  const rows: { c: ZoteroCollectionItem; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    const arr = byParent.get(parent) ?? [];
    for (const c of arr) {
      rows.push({ c, depth });
      walk(c.key, depth + 1);
    }
  };
  walk(null, 0);

  return (
    <div className="max-h-64 space-y-0.5 overflow-y-auto rounded border border-border bg-muted/30 p-2">
      {rows.map(({ c, depth }) => (
        <label
          key={c.key}
          className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted"
          style={{ paddingLeft: `${depth * 12 + 4}px` }}
        >
          <input
            type="checkbox"
            checked={picked.has(c.key)}
            onChange={() => onToggle(c.key)}
          />
          <span>{c.name}</span>
        </label>
      ))}
    </div>
  );
}

interface UnmatchedPolicyProps {
  source: ZoteroSourceResponse;
}

function UnmatchedPolicySection({ source }: UnmatchedPolicyProps) {
  const setPolicy = useSetUnmatchedPolicy();
  const onChange = (next: UnmatchedPolicy) => {
    if (next === source.unmatched_policy) return;
    setPolicy.mutate(next, {
      onError: () =>
        toast.error("Couldn't update unmatched-papers policy"),
    });
  };
  return (
    <div className="space-y-2 rounded border border-border p-3">
      <div className="text-xs font-medium">Unmatched papers</div>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="radio"
          checked={source.unmatched_policy === "tray"}
          onChange={() => onChange("tray")}
          disabled={setPolicy.isPending}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium">Show in tray</span>
          <span className="block text-xs text-muted-foreground">
            Review and add manually.
          </span>
        </span>
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="radio"
          checked={source.unmatched_policy === "skip"}
          onChange={() => onChange("skip")}
          disabled={setPolicy.isPending}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium">Skip silently</span>
          <span className="block text-xs text-muted-foreground">
            Drop if no DOI or title match in your corpus.
          </span>
        </span>
      </label>
      {source.unmatched_policy === "skip" && (
        <p className="pl-6 text-xs text-muted-foreground">
          {source.unmatched_skipped_count} item
          {source.unmatched_skipped_count === 1 ? "" : "s"} skipped so far.
        </p>
      )}
    </div>
  );
}

interface ConnectedProps {
  source: ZoteroSourceResponse;
  onDisconnect: () => void;
}

function formatChecked(ms: number): string {
  const min = Math.round((Date.now() - ms) / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function ConnectedWebApiCard({ source, onDisconnect }: ConnectedProps) {
  const [editing, setEditing] = useState(false);
  const update = useUpdateWatchedCollections();
  const myCollections = useMyCollections(editing);
  const pollNow = usePollNow();
  // The server's real check state, not a countdown (durable jobs, slice 1c).
  const pollStatus = usePollStatus().data;
  const checking = pollNow.isPending || !!pollStatus?.running;
  // A check you asked for, else the last scheduled poll (survives deploys).
  const checkedIso = pollStatus?.completed_at ?? source.last_polled_at;
  const checkedAt = checkedIso ? Date.parse(checkedIso) : null;
  const [picked, setPicked] = useState<Set<string>>(
    new Set(source.watched_collection_ids ?? []),
  );

  const beginEdit = () => {
    setEditing(true);
    setPicked(new Set(source.watched_collection_ids ?? []));
  };

  const onPollNow = async () => {
    try {
      await pollNow.mutateAsync();
    } catch (err) {
      let detail: string | null = null;
      try {
        const httpErr = err as { response?: { json?: () => Promise<{ detail?: string }> } };
        if (httpErr?.response?.json) {
          const body = await httpErr.response.json();
          detail = body?.detail ?? null;
        }
      } catch {
        /* fall through */
      }
      toast.error("Couldn't trigger poll", { description: detail ?? undefined });
    }
  };

  const onSave = async () => {
    try {
      await update.mutateAsync(Array.from(picked));
      toast.success("Watched collections updated");
      setEditing(false);
    } catch {
      toast.error("Couldn't update watched collections");
    }
  };

  const watchedCount = source.watched_collection_ids?.length ?? 0;
  const watchingLabel =
    watchedCount === 0
      ? "the whole library"
      : `${watchedCount} collection${watchedCount === 1 ? "" : "s"}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Cloud className="size-4" /> Cloud (Web API)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Syncing {source.zotero_library_type} library{" "}
          <code className="font-mono">{source.zotero_library_id}</code> — watching{" "}
          <span className="font-medium text-foreground">{watchingLabel}</span>.
          {source.last_polled_at && (
            <> Last polled: {new Date(source.last_polled_at).toLocaleString()}.</>
          )}
        </p>
        {source.status === "needs_reauth" && (
          <p className="text-sm text-destructive">
            Source needs re-authorization: {source.status_reason}
          </p>
        )}
        {!editing && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={onPollNow}
              disabled={checking}
            >
              <RefreshCw className={"size-3.5 " + (checking ? "animate-spin" : "")} />
              {checking ? "Checking…" : "Poll now"}
            </Button>
            {!checking && checkedAt != null && (
              <span className="self-center text-xs text-muted-foreground">
                Checked {formatChecked(checkedAt)}
              </span>
            )}
            <Button size="sm" variant="ghost" onClick={beginEdit}>
              <Edit3 className="size-3.5" /> Edit collections
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => useZoteroBulkImportModalStore.getState().open()}
            >
              <Download className="size-3.5" />
              Import a collection
            </Button>
            <Button size="sm" variant="ghost" onClick={onDisconnect}>
              Disconnect
            </Button>
          </div>
        )}
        {!editing && <UnmatchedPolicySection source={source} />}
        {editing && (
          <div className="space-y-2 rounded border border-border p-3">
            <p className="text-xs text-muted-foreground">
              Empty selection = watch the whole library.
            </p>
            {myCollections.isLoading && (
              <div className="text-xs text-muted-foreground">Loading collections…</div>
            )}
            {myCollections.isError && (
              <div className="text-xs text-destructive">
                Couldn't load collections — try Disconnect + reconnect.
              </div>
            )}
            {myCollections.data && myCollections.data.collections.length === 0 && (
              <div className="text-xs text-muted-foreground">
                No collections in this library.
              </div>
            )}
            {myCollections.data && myCollections.data.collections.length > 0 && (
              <CollectionTree
                collections={myCollections.data.collections}
                picked={picked}
                onToggle={(k) => {
                  const next = new Set(picked);
                  if (next.has(k)) next.delete(k);
                  else next.add(k);
                  setPicked(next);
                }}
              />
            )}
            <div className="flex gap-2">
              <Button size="sm" disabled={update.isPending} onClick={onSave}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
